import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { VegaEmbed } from "react-vega";
import { useGlobalContext } from "../../../context/GlobalContext";
import { getUsageHeatmapData } from "../../../services/dashboard/usageheatmapcharts";
import ChartSkeleton from "../../Skeletons/ChartSkeleton";

const spec = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description:
    "Usage Rhythm Heatmap with hour/daypart toggles + normalization modes.",
  width: 720,
  height: 420,

  params: [
    {
      name: "timeMode",
      value: "hour",
      bind: {
        input: "select",
        options: ["hour", "consumer", "media"],
        labels: ["Hour bins", "Consumer dayparts", "Media dayparts"],
      },
    },
    {
      name: "binSize",
      value: 2,
      bind: {
        input: "select",
        options: [1, 2, 3, 4],
        labels: ["1h", "2h", "3h", "4h"],
      },
    },
    {
      name: "normMode",
      value: "total",
      bind: {
        input: "select",
        options: ["total", "col", "row", "index"],
        labels: [
          "% of week",
          "% within day",
          "% within row",
          "Index vs baseline",
        ],
      },
    },
    {
      name: "minN",
      value: 20,
      bind: { input: "range", min: 0, max: 100, step: 1 },
    },
  ],

  data: {
    name: "events",
  },

  transform: [
    { calculate: "toDate(datum.timestamp_local)", as: "ts" },

    /* Day-of-week (Mon=0..Sun=6) and label */
    { calculate: "(day(datum.ts) + 6) % 7", as: "dow" },
    {
      calculate: "['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][datum.dow]",
      as: "dow_label",
    },

    /* Hour-of-day 0..23 */
    { calculate: "hours(datum.ts)", as: "hour" },

    /* Hour-bin start based on binSize; human-readable label */
    { calculate: "floor(datum.hour / binSize) * binSize", as: "bin_start" },
    {
      calculate: "datum.bin_start + binSize - 1",
      as: "bin_end",
    },
    {
      calculate:
        "binSize==1 ? (datum.bin_start + ':00') : (datum.bin_start + ':00–' + datum.bin_end + ':59')",
      as: "hour_bin_label",
    },
    { calculate: "datum.bin_start", as: "hour_bin_order" },

    /* Consumer routine dayparts (localizable) */
    {
      calculate:
        "(datum.hour>=5 && datum.hour<9) ? 'Early Morning (5–9)' : (datum.hour>=9 && datum.hour<12) ? 'Late Morning (9–12)' : (datum.hour>=12 && datum.hour<16) ? 'Afternoon (12–4)' : (datum.hour>=16 && datum.hour<19) ? 'Early Evening (4–7)' : (datum.hour>=19 && datum.hour<23) ? 'Late Evening (7–11)' : 'Overnight (11–5)'",
      as: "consumer_part",
    },
    {
      calculate:
        "(datum.hour>=5 && datum.hour<9) ? 1 : (datum.hour>=9 && datum.hour<12) ? 2 : (datum.hour>=12 && datum.hour<16) ? 3 : (datum.hour>=16 && datum.hour<19) ? 4 : (datum.hour>=19 && datum.hour<23) ? 5 : 0",
      as: "consumer_order",
    },

    /* Media-style dayparts */
    {
      calculate:
        "(datum.hour>=0 && datum.hour<6) ? 'Overnight (12–6)' : (datum.hour>=6 && datum.hour<9) ? 'Early Morning (6–9)' : (datum.hour>=9 && datum.hour<16) ? 'Daytime (9–4)' : (datum.hour>=16 && datum.hour<19) ? 'Early Fringe (4–7)' : (datum.hour>=19 && datum.hour<23) ? 'Primetime (7–11)' : 'Late Night (11–12)'",
      as: "media_part",
    },
    {
      calculate:
        "(datum.hour<6) ? 0 : (datum.hour<9) ? 1 : (datum.hour<16) ? 2 : (datum.hour<19) ? 3 : (datum.hour<23) ? 4 : 5",
      as: "media_order",
    },

    /* Unified row key/order depending on timeMode */
    {
      calculate:
        "timeMode=='hour' ? datum.hour_bin_label : (timeMode=='consumer' ? datum.consumer_part : datum.media_part)",
      as: "row_key",
    },
    {
      calculate:
        "timeMode=='hour' ? datum.hour_bin_order : (timeMode=='consumer' ? datum.consumer_order : datum.media_order)",
      as: "row_order",
    },

    /* Aggregate to grid cells: n = count of events in each (day,row) */
    {
      aggregate: [{ op: "count", as: "n" }],
      groupby: ["dow", "dow_label", "row_key", "row_order"],
    },

    /* Totals for normalization */
    {
      joinaggregate: [{ op: "sum", field: "n", as: "row_total" }],
      groupby: ["row_key"],
    },
    {
      joinaggregate: [{ op: "sum", field: "n", as: "col_total" }],
      groupby: ["dow"],
    },
    { joinaggregate: [{ op: "sum", field: "n", as: "grand_total" }] },

    /* Shares & index vs independence baseline */
    { calculate: "datum.n / datum.grand_total", as: "share_total" },
    { calculate: "datum.n / datum.col_total", as: "share_col" },
    { calculate: "datum.n / datum.row_total", as: "share_row" },
    {
      calculate:
        "(datum.row_total/datum.grand_total) * (datum.col_total/datum.grand_total)",
      as: "expected_independence",
    },
    {
      calculate: "datum.share_total / datum.expected_independence",
      as: "index_lift",
    },

    /* Choose the rendered value based on normalization mode */
    {
      calculate:
        "normMode=='total' ? datum.share_total : (normMode=='col' ? datum.share_col : (normMode=='row' ? datum.share_row : datum.index_lift))",
      as: "z",
    },

    /* Flag low sample */
    { calculate: "datum.n < minN", as: "is_low_n" },
  ],

  encoding: {
    x: {
      field: "dow_label",
      type: "nominal",
      title: "Day of Week",
      sort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      axis: { labelAngle: 0 },
    },
    y: {
      field: "row_key",
      type: "ordinal",
      title: {
        signal:
          "timeMode=='hour' ? 'Time Bin' : (timeMode=='consumer' ? 'Consumer Daypart' : 'Media Daypart')",
      },
      sort: { field: "row_order", order: "ascending" },
    },
    tooltip: [
      { field: "dow_label", type: "nominal", title: "Day" },
      {
        field: "row_key",
        type: "nominal",
        title: {
          signal:
            "timeMode=='hour' ? 'Time Bin' : (timeMode=='consumer' ? 'Daypart (Consumer)' : 'Daypart (Media)')",
        },
      },
      { field: "n", type: "quantitative", title: "Occasions (n)" },
      {
        field: "share_total",
        type: "quantitative",
        title: "% of Week",
        format: ".1%",
      },
      {
        field: "share_col",
        type: "quantitative",
        title: "% within Day",
        format: ".1%",
      },
      {
        field: "share_row",
        type: "quantitative",
        title: "% within Row",
        format: ".1%",
      },
      {
        field: "index_lift",
        type: "quantitative",
        title: "Index vs. Baseline",
        format: ".2f",
      },
    ],
  },

  layer: [
    /* Percent modes layer (total/col/row) — sequential palette */
    {
      transform: [{ filter: "normMode!='index'" }],
      mark: { type: "rect" },
      encoding: {
        color: {
          field: "z",
          type: "quantitative",
          title: {
            signal:
              "normMode=='total' ? '% of Week' : (normMode=='col' ? '% within Day' : '% within Row')",
          },
          scale: { scheme: "blues" },
          legend: { format: ".0%" },
        },
        opacity: {
          condition: { test: "!datum.is_low_n", value: 1.0 },
          value: 0.25,
        },
      },
    },

    /* Index mode layer — diverging palette centered at 1 */
    {
      transform: [{ filter: "normMode=='index'" }],
      mark: { type: "rect" },
      encoding: {
        color: {
          field: "z",
          type: "quantitative",
          title: "Index vs Baseline",
          scale: { scheme: "redblue", domainMid: 1 },
          legend: { format: ".2f" },
        },
        opacity: {
          condition: { test: "!datum.is_low_n", value: 1.0 },
          value: 0.25,
        },
      },
    },
  ],
};

// Sample mock data
const mockData = [
  {
    timestamp_local: "2025-02-25T06:34:39",
    participant_id: "54695",
    brand: "Febreze",
    duration: "144",
  },
  {
    timestamp_local: "2025-02-25T07:15:11",
    participant_id: "54695",
    brand: "Febreze",
    duration: "35",
  },
  {
    timestamp_local: "2025-02-25T08:00:44",
    participant_id: "54608",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-02-25T08:25:48",
    participant_id: "10601",
    brand: "Febreze",
    duration: "41",
  },
  {
    timestamp_local: "2025-02-25T08:54:27",
    participant_id: "54619",
    brand: "Febreze",
    duration: "59",
  },
  {
    timestamp_local: "2025-02-25T10:09:06",
    participant_id: "54626",
    brand: "",
    duration: "90",
  },
  {
    timestamp_local: "2025-02-25T10:31:10",
    participant_id: "54658",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-02-25T10:44:32",
    participant_id: "34947",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-02-25T11:06:33",
    participant_id: "15419",
    brand: "Febreze",
    duration: "228",
  },
  {
    timestamp_local: "2025-02-25T11:07:09",
    participant_id: "54622",
    brand: "Febreze",
    duration: "82",
  },
  {
    timestamp_local: "2025-02-25T12:20:30",
    participant_id: "54637",
    brand: "Febreze",
    duration: "10",
  },
  {
    timestamp_local: "2025-02-25T15:03:36",
    participant_id: "10601",
    brand: "Febreze",
    duration: "61",
  },
  {
    timestamp_local: "2025-02-25T15:04:27",
    participant_id: "54633",
    brand: "Febreze",
    duration: "34",
  },
  {
    timestamp_local: "2025-02-25T15:42:52",
    participant_id: "54625",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-02-25T15:54:53",
    participant_id: "15419",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-02-25T16:36:45",
    participant_id: "54618",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-02-25T17:00:20",
    participant_id: "54608",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-02-25T17:19:30",
    participant_id: "54619",
    brand: "Febreze",
    duration: "49",
  },
  {
    timestamp_local: "2025-02-25T17:24:44",
    participant_id: "54601",
    brand: "Febreze",
    duration: "224",
  },
  {
    timestamp_local: "2025-02-25T17:49:40",
    participant_id: "54695",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-02-25T17:52:37",
    participant_id: "51135",
    brand: "Febreze",
    duration: "94",
  },
  {
    timestamp_local: "2025-02-25T18:47:44",
    participant_id: "39185",
    brand: "Febreze",
    duration: "240",
  },
  {
    timestamp_local: "2025-02-25T19:13:35",
    participant_id: "27064",
    brand: "Febreze",
    duration: "73",
  },
  {
    timestamp_local: "2025-02-25T19:55:35",
    participant_id: "53944",
    brand: "Febreze",
    duration: "44",
  },
  {
    timestamp_local: "2025-02-25T20:08:24",
    participant_id: "54631",
    brand: "Febreze",
    duration: "42",
  },
  {
    timestamp_local: "2025-02-25T20:22:08",
    participant_id: "54633",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-02-25T21:17:39",
    participant_id: "54637",
    brand: "Febreze",
    duration: "195",
  },
  {
    timestamp_local: "2025-02-25T21:28:54",
    participant_id: "54659",
    brand: "Febreze",
    duration: "109",
  },
  {
    timestamp_local: "2025-02-25T21:37:52",
    participant_id: "6169",
    brand: "Febreze",
    duration: "192",
  },
  {
    timestamp_local: "2025-02-26T06:55:56",
    participant_id: "6169",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-02-26T07:07:31",
    participant_id: "6169",
    brand: "Febreze",
    duration: "34",
  },
  {
    timestamp_local: "2025-02-26T08:57:34",
    participant_id: "54608",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-02-26T09:03:52",
    participant_id: "54658",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-02-26T09:05:15",
    participant_id: "54619",
    brand: "Febreze",
    duration: "34",
  },
  {
    timestamp_local: "2025-02-26T09:08:50",
    participant_id: "15419",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-02-26T11:01:11",
    participant_id: "54671",
    brand: "Febreze",
    duration: "113",
  },
  {
    timestamp_local: "2025-02-26T11:12:50",
    participant_id: "54695",
    brand: "Febreze",
    duration: "245",
  },
  {
    timestamp_local: "2025-02-26T12:23:53",
    participant_id: "54619",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-02-26T13:32:55",
    participant_id: "54638",
    brand: "Febreze",
    duration: "80",
  },
  {
    timestamp_local: "2025-02-26T13:38:02",
    participant_id: "54601",
    brand: "Febreze",
    duration: "59",
  },
  {
    timestamp_local: "2025-02-26T13:40:35",
    participant_id: "54618",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-02-26T14:18:59",
    participant_id: "15419",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-02-26T15:15:18",
    participant_id: "54625",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-02-26T15:24:21",
    participant_id: "54619",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-02-26T15:53:55",
    participant_id: "54655",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-02-26T16:56:41",
    participant_id: "34947",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-02-26T17:42:51",
    participant_id: "54626",
    brand: "",
    duration: "64",
  },
  {
    timestamp_local: "2025-02-26T18:13:04",
    participant_id: "38002",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-02-26T18:23:34",
    participant_id: "54623",
    brand: "Febreze",
    duration: "11",
  },
  {
    timestamp_local: "2025-02-26T19:53:24",
    participant_id: "15419",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-02-26T20:01:11",
    participant_id: "10601",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-02-26T20:27:40",
    participant_id: "53944",
    brand: "Febreze",
    duration: "49",
  },
  {
    timestamp_local: "2025-02-26T21:06:16",
    participant_id: "38002",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-02-26T21:10:38",
    participant_id: "20130",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-02-26T21:19:30",
    participant_id: "54655",
    brand: "Febreze",
    duration: "56",
  },
  {
    timestamp_local: "2025-02-26T22:02:23",
    participant_id: "6169",
    brand: "Febreze",
    duration: "39",
  },
  {
    timestamp_local: "2025-02-27T06:46:50",
    participant_id: "54652",
    brand: "Febreze",
    duration: "248",
  },
  {
    timestamp_local: "2025-02-27T07:16:33",
    participant_id: "54618",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-02-27T08:04:19",
    participant_id: "54608",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-02-27T08:39:40",
    participant_id: "54635",
    brand: "Febreze",
    duration: "85",
  },
  {
    timestamp_local: "2025-02-27T09:02:58",
    participant_id: "15419",
    brand: "Febreze",
    duration: "43",
  },
  {
    timestamp_local: "2025-02-27T09:48:29",
    participant_id: "54637",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-02-27T10:22:58",
    participant_id: "51135",
    brand: "Febreze",
    duration: "63",
  },
  {
    timestamp_local: "2025-02-27T10:29:44",
    participant_id: "6169",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-02-27T11:04:55",
    participant_id: "38002",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-02-27T12:45:07",
    participant_id: "54659",
    brand: "Febreze",
    duration: "50",
  },
  {
    timestamp_local: "2025-02-27T13:46:03",
    participant_id: "54638",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-02-27T15:06:43",
    participant_id: "54626",
    brand: "",
    duration: "57",
  },
  {
    timestamp_local: "2025-02-27T16:28:44",
    participant_id: "54625",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-02-27T16:49:06",
    participant_id: "54655",
    brand: "Febreze",
    duration: "68",
  },
  {
    timestamp_local: "2025-02-27T16:57:41",
    participant_id: "54658",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-02-27T17:32:47",
    participant_id: "38002",
    brand: "Febreze",
    duration: "58",
  },
  {
    timestamp_local: "2025-02-27T17:45:00",
    participant_id: "54619",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-02-27T19:28:00",
    participant_id: "20130",
    brand: "Febreze",
    duration: "39",
  },
  {
    timestamp_local: "2025-02-27T19:31:59",
    participant_id: "15419",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-02-27T19:45:59",
    participant_id: "20130",
    brand: "Febreze",
    duration: "151",
  },
  {
    timestamp_local: "2025-02-27T19:47:30",
    participant_id: "54633",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-02-27T21:11:14",
    participant_id: "54631",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-02-27T21:44:50",
    participant_id: "10601",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-02-28T06:57:59",
    participant_id: "54618",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-02-28T08:22:17",
    participant_id: "39185",
    brand: "Febreze",
    duration: "69",
  },
  {
    timestamp_local: "2025-02-28T08:26:11",
    participant_id: "54608",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-02-28T08:33:48",
    participant_id: "54601",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-02-28T09:52:41",
    participant_id: "43663",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-02-28T10:06:58",
    participant_id: "54638",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-02-28T10:23:53",
    participant_id: "10601",
    brand: "Febreze",
    duration: "44",
  },
  {
    timestamp_local: "2025-02-28T10:25:51",
    participant_id: "54633",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-02-28T10:57:15",
    participant_id: "54623",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-02-28T11:37:24",
    participant_id: "54625",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-02-28T12:14:40",
    participant_id: "54622",
    brand: "Febreze",
    duration: "34",
  },
  {
    timestamp_local: "2025-02-28T12:43:32",
    participant_id: "54601",
    brand: "Febreze",
    duration: "46",
  },
  {
    timestamp_local: "2025-02-28T13:15:37",
    participant_id: "54623",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-02-28T14:29:24",
    participant_id: "54658",
    brand: "Febreze",
    duration: "50",
  },
  {
    timestamp_local: "2025-02-28T14:47:03",
    participant_id: "15419",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-02-28T14:52:47",
    participant_id: "54635",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-02-28T15:42:19",
    participant_id: "6169",
    brand: "Febreze",
    duration: "107",
  },
  {
    timestamp_local: "2025-02-28T16:19:56",
    participant_id: "38002",
    brand: "Febreze",
    duration: "29",
  },
  {
    timestamp_local: "2025-02-28T16:38:39",
    participant_id: "54671",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-02-28T17:21:53",
    participant_id: "6169",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-02-28T17:31:08",
    participant_id: "51135",
    brand: "Febreze",
    duration: "60",
  },
  {
    timestamp_local: "2025-02-28T17:54:38",
    participant_id: "54619",
    brand: "Febreze",
    duration: "9",
  },
  {
    timestamp_local: "2025-02-28T18:03:15",
    participant_id: "54618",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-02-28T18:18:16",
    participant_id: "54623",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-02-28T19:51:21",
    participant_id: "54659",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-02-28T20:18:38",
    participant_id: "54631",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-02-28T20:22:02",
    participant_id: "53944",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-02-28T22:18:07",
    participant_id: "10601",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-01T07:11:58",
    participant_id: "54659",
    brand: "Febreze",
    duration: "40",
  },
  {
    timestamp_local: "2025-03-01T07:20:26",
    participant_id: "6169",
    brand: "Febreze",
    duration: "35",
  },
  {
    timestamp_local: "2025-03-01T08:07:17",
    participant_id: "54695",
    brand: "Febreze",
    duration: "166",
  },
  {
    timestamp_local: "2025-03-01T10:04:10",
    participant_id: "6169",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-01T10:58:52",
    participant_id: "54637",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-01T11:26:23",
    participant_id: "10601",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-01T11:36:08",
    participant_id: "54671",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-03-01T11:54:05",
    participant_id: "54601",
    brand: "Febreze",
    duration: "54",
  },
  {
    timestamp_local: "2025-03-01T12:13:26",
    participant_id: "54619",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-03-01T13:21:13",
    participant_id: "34947",
    brand: "Febreze",
    duration: "35",
  },
  {
    timestamp_local: "2025-03-01T13:40:00",
    participant_id: "54608",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-01T15:57:47",
    participant_id: "54618",
    brand: "Febreze",
    duration: "0",
  },
  {
    timestamp_local: "2025-03-01T16:12:12",
    participant_id: "54659",
    brand: "Febreze",
    duration: "38",
  },
  {
    timestamp_local: "2025-03-01T16:47:27",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-01T17:10:06",
    participant_id: "27064",
    brand: "Febreze",
    duration: "5",
  },
  {
    timestamp_local: "2025-03-01T17:47:03",
    participant_id: "54659",
    brand: "Febreze",
    duration: "78",
  },
  {
    timestamp_local: "2025-03-01T18:11:42",
    participant_id: "10601",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-01T18:29:17",
    participant_id: "6169",
    brand: "Febreze",
    duration: "34",
  },
  {
    timestamp_local: "2025-03-01T18:34:15",
    participant_id: "54622",
    brand: "Febreze",
    duration: "48",
  },
  {
    timestamp_local: "2025-03-01T20:12:23",
    participant_id: "54608",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-02T06:04:46",
    participant_id: "54695",
    brand: "Febreze",
    duration: "55",
  },
  {
    timestamp_local: "2025-03-02T09:22:39",
    participant_id: "54626",
    brand: "",
    duration: "49",
  },
  {
    timestamp_local: "2025-03-02T09:35:42",
    participant_id: "15419",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-02T09:45:42",
    participant_id: "38002",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-03-02T09:46:01",
    participant_id: "53944",
    brand: "Febreze",
    duration: "29",
  },
  {
    timestamp_local: "2025-03-02T09:52:22",
    participant_id: "39185",
    brand: "Febreze",
    duration: "39",
  },
  {
    timestamp_local: "2025-03-02T11:27:34",
    participant_id: "54638",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-02T11:34:50",
    participant_id: "54695",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-02T11:54:28",
    participant_id: "54619",
    brand: "Febreze",
    duration: "37",
  },
  {
    timestamp_local: "2025-03-02T12:49:27",
    participant_id: "54618",
    brand: "Febreze",
    duration: "54",
  },
  {
    timestamp_local: "2025-03-02T12:51:50",
    participant_id: "54608",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-02T14:30:50",
    participant_id: "54601",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-02T15:46:17",
    participant_id: "54619",
    brand: "Febreze",
    duration: "39",
  },
  {
    timestamp_local: "2025-03-02T16:56:10",
    participant_id: "54631",
    brand: "Febreze",
    duration: "40",
  },
  {
    timestamp_local: "2025-03-02T17:47:50",
    participant_id: "10601",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-02T18:41:24",
    participant_id: "54626",
    brand: "",
    duration: "48",
  },
  {
    timestamp_local: "2025-03-02T20:30:18",
    participant_id: "43663",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-02T21:02:42",
    participant_id: "54635",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-02T22:26:31",
    participant_id: "54635",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-03T07:29:06",
    participant_id: "34947",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-03T08:10:05",
    participant_id: "54608",
    brand: "Febreze",
    duration: "24",
  },
  {
    timestamp_local: "2025-03-03T09:07:58",
    participant_id: "15419",
    brand: "Febreze",
    duration: "11",
  },
  {
    timestamp_local: "2025-03-03T09:09:45",
    participant_id: "54619",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-03T09:13:38",
    participant_id: "51135",
    brand: "Febreze",
    duration: "51",
  },
  {
    timestamp_local: "2025-03-03T09:42:19",
    participant_id: "54618",
    brand: "Febreze",
    duration: "11",
  },
  {
    timestamp_local: "2025-03-03T10:16:23",
    participant_id: "54637",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-03T10:44:40",
    participant_id: "6169",
    brand: "Febreze",
    duration: "53",
  },
  {
    timestamp_local: "2025-03-03T10:53:22",
    participant_id: "54625",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-03-03T11:00:32",
    participant_id: "54623",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-03T12:29:18",
    participant_id: "54619",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-03T13:27:26",
    participant_id: "38002",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-03T13:42:16",
    participant_id: "10601",
    brand: "Febreze",
    duration: "68",
  },
  {
    timestamp_local: "2025-03-03T14:34:31",
    participant_id: "10601",
    brand: "Febreze",
    duration: "61",
  },
  {
    timestamp_local: "2025-03-03T15:58:32",
    participant_id: "54619",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-03T16:24:35",
    participant_id: "27064",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-03T17:00:00",
    participant_id: "15419",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-03T17:19:10",
    participant_id: "54659",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-03T19:31:00",
    participant_id: "54633",
    brand: "Febreze",
    duration: "89",
  },
  {
    timestamp_local: "2025-03-04T06:14:41",
    participant_id: "6169",
    brand: "Febreze",
    duration: "35",
  },
  {
    timestamp_local: "2025-03-04T07:47:01",
    participant_id: "54608",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-04T08:52:16",
    participant_id: "54623",
    brand: "Febreze",
    duration: "193",
  },
  {
    timestamp_local: "2025-03-04T08:59:07",
    participant_id: "54652",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-04T09:10:15",
    participant_id: "54619",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-04T10:53:28",
    participant_id: "54637",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-04T11:33:05",
    participant_id: "54671",
    brand: "Febreze",
    duration: "33",
  },
  {
    timestamp_local: "2025-03-04T12:05:02",
    participant_id: "54618",
    brand: "Febreze",
    duration: "29",
  },
  {
    timestamp_local: "2025-03-04T12:27:54",
    participant_id: "10601",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-04T13:44:06",
    participant_id: "15419",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-04T13:56:07",
    participant_id: "15419",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-04T16:10:30",
    participant_id: "54619",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-04T16:31:57",
    participant_id: "54659",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-04T16:36:37",
    participant_id: "54622",
    brand: "Febreze",
    duration: "41",
  },
  {
    timestamp_local: "2025-03-04T17:12:12",
    participant_id: "54633",
    brand: "Febreze",
    duration: "76",
  },
  {
    timestamp_local: "2025-03-04T17:14:33",
    participant_id: "54695",
    brand: "Febreze",
    duration: "66",
  },
  {
    timestamp_local: "2025-03-04T17:27:42",
    participant_id: "54635",
    brand: "Febreze",
    duration: "6",
  },
  {
    timestamp_local: "2025-03-04T17:41:08",
    participant_id: "54658",
    brand: "Febreze",
    duration: "33",
  },
  {
    timestamp_local: "2025-03-04T17:59:46",
    participant_id: "51135",
    brand: "Febreze",
    duration: "38",
  },
  {
    timestamp_local: "2025-03-04T18:47:23",
    participant_id: "38002",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-04T18:55:03",
    participant_id: "54601",
    brand: "Febreze",
    duration: "65",
  },
  {
    timestamp_local: "2025-03-04T19:02:52",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-04T19:31:46",
    participant_id: "20130",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-04T20:06:07",
    participant_id: "53944",
    brand: "Febreze",
    duration: "31",
  },
  {
    timestamp_local: "2025-03-04T20:40:47",
    participant_id: "54631",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-04T21:22:11",
    participant_id: "54626",
    brand: "",
    duration: "87",
  },
  {
    timestamp_local: "2025-03-05T00:43:40",
    participant_id: "43663",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-05T07:11:49",
    participant_id: "53944",
    brand: "Febreze",
    duration: "33",
  },
  {
    timestamp_local: "2025-03-05T07:52:50",
    participant_id: "54626",
    brand: "",
    duration: "68",
  },
  {
    timestamp_local: "2025-03-05T07:58:37",
    participant_id: "54608",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-05T08:00:05",
    participant_id: "27064",
    brand: "Febreze",
    duration: "5",
  },
  {
    timestamp_local: "2025-03-05T08:06:52",
    participant_id: "6169",
    brand: "Febreze",
    duration: "33",
  },
  {
    timestamp_local: "2025-03-05T08:20:15",
    participant_id: "54626",
    brand: "",
    duration: "11",
  },
  {
    timestamp_local: "2025-03-05T08:52:11",
    participant_id: "10601",
    brand: "Febreze",
    duration: "50",
  },
  {
    timestamp_local: "2025-03-05T09:42:23",
    participant_id: "54619",
    brand: "Febreze",
    duration: "11",
  },
  {
    timestamp_local: "2025-03-05T11:55:06",
    participant_id: "38002",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-05T12:14:51",
    participant_id: "54637",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-05T14:24:42",
    participant_id: "54635",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-05T15:04:59",
    participant_id: "54633",
    brand: "Febreze",
    duration: "43",
  },
  {
    timestamp_local: "2025-03-05T16:37:17",
    participant_id: "54625",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-05T17:41:02",
    participant_id: "54618",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-05T19:24:06",
    participant_id: "54659",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-05T19:54:49",
    participant_id: "53944",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-05T20:27:50",
    participant_id: "54631",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-05T22:37:09",
    participant_id: "54659",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-06T06:57:09",
    participant_id: "6169",
    brand: "Febreze",
    duration: "39",
  },
  {
    timestamp_local: "2025-03-06T07:12:40",
    participant_id: "54618",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-06T07:46:38",
    participant_id: "54608",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-06T07:49:51",
    participant_id: "54635",
    brand: "Febreze",
    duration: "29",
  },
  {
    timestamp_local: "2025-03-06T08:47:09",
    participant_id: "54619",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-06T08:59:57",
    participant_id: "27064",
    brand: "Febreze",
    duration: "2",
  },
  {
    timestamp_local: "2025-03-06T09:17:26",
    participant_id: "15419",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-06T09:29:00",
    participant_id: "43663",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-06T11:10:40",
    participant_id: "54623",
    brand: "Febreze",
    duration: "53",
  },
  {
    timestamp_local: "2025-03-06T11:14:37",
    participant_id: "54622",
    brand: "Febreze",
    duration: "38",
  },
  {
    timestamp_local: "2025-03-06T11:27:58",
    participant_id: "10601",
    brand: "Febreze",
    duration: "40",
  },
  {
    timestamp_local: "2025-03-06T12:41:10",
    participant_id: "54638",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-06T13:32:04",
    participant_id: "51135",
    brand: "Febreze",
    duration: "73",
  },
  {
    timestamp_local: "2025-03-06T14:50:09",
    participant_id: "54626",
    brand: "",
    duration: "66",
  },
  {
    timestamp_local: "2025-03-06T15:21:08",
    participant_id: "54635",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-06T16:38:01",
    participant_id: "54625",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-06T16:51:20",
    participant_id: "6169",
    brand: "Febreze",
    duration: "34",
  },
  {
    timestamp_local: "2025-03-06T17:06:28",
    participant_id: "54601",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-06T17:16:24",
    participant_id: "38002",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-06T17:17:44",
    participant_id: "15419",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-06T17:28:32",
    participant_id: "54626",
    brand: "",
    duration: "54",
  },
  {
    timestamp_local: "2025-03-06T18:50:10",
    participant_id: "10601",
    brand: "Febreze",
    duration: "49",
  },
  {
    timestamp_local: "2025-03-06T19:52:39",
    participant_id: "53944",
    brand: "Febreze",
    duration: "31",
  },
  {
    timestamp_local: "2025-03-06T20:04:48",
    participant_id: "54658",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-06T22:31:52",
    participant_id: "53944",
    brand: "Febreze",
    duration: "52",
  },
  {
    timestamp_local: "2025-03-07T07:41:50",
    participant_id: "54638",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-07T08:04:00",
    participant_id: "54608",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-07T08:14:22",
    participant_id: "54622",
    brand: "Febreze",
    duration: "39",
  },
  {
    timestamp_local: "2025-03-07T08:37:06",
    participant_id: "10601",
    brand: "Febreze",
    duration: "52",
  },
  {
    timestamp_local: "2025-03-07T09:59:04",
    participant_id: "54633",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-07T10:12:30",
    participant_id: "54637",
    brand: "Febreze",
    duration: "51",
  },
  {
    timestamp_local: "2025-03-07T11:09:14",
    participant_id: "54601",
    brand: "Febreze",
    duration: "71",
  },
  {
    timestamp_local: "2025-03-07T11:15:53",
    participant_id: "15419",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-07T11:43:35",
    participant_id: "54619",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-07T12:24:29",
    participant_id: "10601",
    brand: "Febreze",
    duration: "31",
  },
  {
    timestamp_local: "2025-03-07T12:58:24",
    participant_id: "54626",
    brand: "",
    duration: "72",
  },
  {
    timestamp_local: "2025-03-07T14:33:50",
    participant_id: "38002",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-07T15:17:54",
    participant_id: "6169",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-07T16:46:49",
    participant_id: "43663",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-07T17:37:08",
    participant_id: "54633",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-07T17:42:42",
    participant_id: "54623",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-07T18:00:25",
    participant_id: "54618",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-07T18:19:44",
    participant_id: "54671",
    brand: "Febreze",
    duration: "31",
  },
  {
    timestamp_local: "2025-03-07T19:25:56",
    participant_id: "54655",
    brand: "Febreze",
    duration: "85",
  },
  {
    timestamp_local: "2025-03-07T20:01:20",
    participant_id: "54631",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-07T20:41:41",
    participant_id: "15419",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-07T22:17:19",
    participant_id: "20130",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-07T22:19:23",
    participant_id: "54659",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-08T07:10:04",
    participant_id: "54618",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-08T07:28:03",
    participant_id: "54635",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-08T08:14:11",
    participant_id: "53944",
    brand: "Febreze",
    duration: "47",
  },
  {
    timestamp_local: "2025-03-08T08:42:29",
    participant_id: "54695",
    brand: "Febreze",
    duration: "78",
  },
  {
    timestamp_local: "2025-03-08T08:53:22",
    participant_id: "6169",
    brand: "Febreze",
    duration: "37",
  },
  {
    timestamp_local: "2025-03-08T11:14:53",
    participant_id: "54608",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-08T11:43:53",
    participant_id: "54635",
    brand: "Febreze",
    duration: "1",
  },
  {
    timestamp_local: "2025-03-08T12:25:21",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-08T13:10:30",
    participant_id: "54659",
    brand: "Febreze",
    duration: "41",
  },
  {
    timestamp_local: "2025-03-08T13:46:15",
    participant_id: "54659",
    brand: "Febreze",
    duration: "61",
  },
  {
    timestamp_local: "2025-03-08T15:03:03",
    participant_id: "10601",
    brand: "Febreze",
    duration: "57",
  },
  {
    timestamp_local: "2025-03-08T15:26:23",
    participant_id: "54658",
    brand: "Febreze",
    duration: "139",
  },
  {
    timestamp_local: "2025-03-08T17:25:31",
    participant_id: "38002",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-08T17:27:27",
    participant_id: "51135",
    brand: "Febreze",
    duration: "65",
  },
  {
    timestamp_local: "2025-03-08T20:37:40",
    participant_id: "54633",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-09T08:00:59",
    participant_id: "54638",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-09T08:27:15",
    participant_id: "54608",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-09T08:45:31",
    participant_id: "15419",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-09T09:56:16",
    participant_id: "27064",
    brand: "Febreze",
    duration: "0",
  },
  {
    timestamp_local: "2025-03-09T10:01:21",
    participant_id: "34947",
    brand: "Febreze",
    duration: "4",
  },
  {
    timestamp_local: "2025-03-09T10:18:58",
    participant_id: "38002",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-09T11:06:40",
    participant_id: "10601",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-09T11:30:51",
    participant_id: "54619",
    brand: "Febreze",
    duration: "51",
  },
  {
    timestamp_local: "2025-03-09T15:58:00",
    participant_id: "54625",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-09T17:06:02",
    participant_id: "54635",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-09T17:11:28",
    participant_id: "54637",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-09T17:19:43",
    participant_id: "54619",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-09T17:22:16",
    participant_id: "51135",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-09T17:25:10",
    participant_id: "54618",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-09T17:34:47",
    participant_id: "54633",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-09T18:22:21",
    participant_id: "20130",
    brand: "Febreze",
    duration: "75",
  },
  {
    timestamp_local: "2025-03-09T19:31:56",
    participant_id: "43663",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-09T19:32:00",
    participant_id: "54626",
    brand: "",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-09T19:45:38",
    participant_id: "54659",
    brand: "Febreze",
    duration: "91",
  },
  {
    timestamp_local: "2025-03-09T20:58:12",
    participant_id: "54659",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-10T07:30:39",
    participant_id: "54635",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-10T07:55:19",
    participant_id: "54608",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-10T08:37:17",
    participant_id: "15419",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-10T09:40:21",
    participant_id: "54619",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-10T10:34:57",
    participant_id: "54637",
    brand: "Febreze",
    duration: "38",
  },
  {
    timestamp_local: "2025-03-10T12:39:02",
    participant_id: "10601",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-10T13:50:20",
    participant_id: "54623",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-10T15:31:20",
    participant_id: "54619",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-10T16:01:12",
    participant_id: "54658",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-10T16:49:21",
    participant_id: "54622",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-10T17:33:29",
    participant_id: "54618",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-10T17:35:04",
    participant_id: "54652",
    brand: "Febreze",
    duration: "51",
  },
  {
    timestamp_local: "2025-03-10T17:45:10",
    participant_id: "54601",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-10T18:39:01",
    participant_id: "53944",
    brand: "Febreze",
    duration: "24",
  },
  {
    timestamp_local: "2025-03-10T18:41:18",
    participant_id: "54659",
    brand: "Febreze",
    duration: "72",
  },
  {
    timestamp_local: "2025-03-10T19:55:29",
    participant_id: "54659",
    brand: "Febreze",
    duration: "51",
  },
  {
    timestamp_local: "2025-03-10T20:00:25",
    participant_id: "54631",
    brand: "Febreze",
    duration: "31",
  },
  {
    timestamp_local: "2025-03-10T20:23:21",
    participant_id: "54635",
    brand: "Febreze",
    duration: "11",
  },
  {
    timestamp_local: "2025-03-10T20:23:46",
    participant_id: "15419",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-11T05:05:14",
    participant_id: "6169",
    brand: "Febreze",
    duration: "41",
  },
  {
    timestamp_local: "2025-03-11T07:28:54",
    participant_id: "54635",
    brand: "Febreze",
    duration: "11",
  },
  {
    timestamp_local: "2025-03-11T07:43:11",
    participant_id: "43663",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-11T07:51:24",
    participant_id: "54608",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-11T08:12:09",
    participant_id: "10601",
    brand: "Febreze",
    duration: "65",
  },
  {
    timestamp_local: "2025-03-11T08:40:31",
    participant_id: "54619",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-11T10:04:49",
    participant_id: "15419",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-11T10:24:45",
    participant_id: "54659",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-11T13:44:58",
    participant_id: "54659",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-11T13:50:25",
    participant_id: "39185",
    brand: "Febreze",
    duration: "43",
  },
  {
    timestamp_local: "2025-03-11T14:14:14",
    participant_id: "54671",
    brand: "Febreze",
    duration: "65",
  },
  {
    timestamp_local: "2025-03-11T15:38:26",
    participant_id: "54619",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-11T15:49:26",
    participant_id: "38002",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-11T18:02:24",
    participant_id: "54625",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-11T18:27:21",
    participant_id: "54695",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-11T19:59:45",
    participant_id: "54618",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-11T19:59:57",
    participant_id: "15419",
    brand: "Febreze",
    duration: "44",
  },
  {
    timestamp_local: "2025-03-12T07:52:14",
    participant_id: "54608",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-12T08:08:45",
    participant_id: "54626",
    brand: "",
    duration: "29",
  },
  {
    timestamp_local: "2025-03-12T09:04:58",
    participant_id: "10601",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-12T09:14:50",
    participant_id: "51135",
    brand: "Febreze",
    duration: "62",
  },
  {
    timestamp_local: "2025-03-12T09:32:59",
    participant_id: "54619",
    brand: "Febreze",
    duration: "49",
  },
  {
    timestamp_local: "2025-03-12T11:41:08",
    participant_id: "54695",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-12T12:11:35",
    participant_id: "54637",
    brand: "Febreze",
    duration: "53",
  },
  {
    timestamp_local: "2025-03-12T12:54:18",
    participant_id: "54659",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-12T13:25:33",
    participant_id: "54601",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-12T13:42:57",
    participant_id: "54633",
    brand: "Febreze",
    duration: "41",
  },
  {
    timestamp_local: "2025-03-12T16:25:17",
    participant_id: "38002",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-12T18:12:38",
    participant_id: "54618",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-12T19:50:41",
    participant_id: "53944",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-12T20:11:56",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-13T07:53:13",
    participant_id: "54608",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-13T08:43:36",
    participant_id: "10601",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-13T09:50:27",
    participant_id: "54619",
    brand: "Febreze",
    duration: "67",
  },
  {
    timestamp_local: "2025-03-13T10:48:17",
    participant_id: "54635",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-13T11:38:45",
    participant_id: "54695",
    brand: "Febreze",
    duration: "265",
  },
  {
    timestamp_local: "2025-03-13T13:09:51",
    participant_id: "54671",
    brand: "Febreze",
    duration: "80",
  },
  {
    timestamp_local: "2025-03-13T13:10:00",
    participant_id: "34947",
    brand: "Febreze",
    duration: "4",
  },
  {
    timestamp_local: "2025-03-13T13:40:07",
    participant_id: "15419",
    brand: "Febreze",
    duration: "34",
  },
  {
    timestamp_local: "2025-03-13T15:09:32",
    participant_id: "54619",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-13T15:31:28",
    participant_id: "15419",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-13T16:23:54",
    participant_id: "54652",
    brand: "Febreze",
    duration: "109",
  },
  {
    timestamp_local: "2025-03-13T17:15:35",
    participant_id: "54633",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-13T17:55:19",
    participant_id: "54623",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-13T20:00:16",
    participant_id: "51135",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-03-13T20:12:55",
    participant_id: "54659",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-14T07:27:43",
    participant_id: "53944",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-14T07:48:00",
    participant_id: "27064",
    brand: "Febreze",
    duration: "10",
  },
  {
    timestamp_local: "2025-03-14T07:57:57",
    participant_id: "54608",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-14T08:28:10",
    participant_id: "54618",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-14T10:24:41",
    participant_id: "54637",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-14T11:37:03",
    participant_id: "54658",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-14T12:00:41",
    participant_id: "54619",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-14T12:01:29",
    participant_id: "54601",
    brand: "Febreze",
    duration: "140",
  },
  {
    timestamp_local: "2025-03-14T12:13:36",
    participant_id: "38002",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-14T14:11:41",
    participant_id: "54623",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-14T14:46:50",
    participant_id: "54635",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-03-14T16:26:51",
    participant_id: "39185",
    brand: "Febreze",
    duration: "31",
  },
  {
    timestamp_local: "2025-03-14T17:07:27",
    participant_id: "54631",
    brand: "Febreze",
    duration: "31",
  },
  {
    timestamp_local: "2025-03-14T17:09:45",
    participant_id: "10601",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-14T17:40:25",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-14T18:23:04",
    participant_id: "54622",
    brand: "Febreze",
    duration: "39",
  },
  {
    timestamp_local: "2025-03-15T08:31:51",
    participant_id: "51135",
    brand: "Febreze",
    duration: "50",
  },
  {
    timestamp_local: "2025-03-15T09:37:48",
    participant_id: "15419",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-15T11:22:11",
    participant_id: "54659",
    brand: "Febreze",
    duration: "43",
  },
  {
    timestamp_local: "2025-03-15T11:55:16",
    participant_id: "54608",
    brand: "Febreze",
    duration: "10",
  },
  {
    timestamp_local: "2025-03-15T11:59:24",
    participant_id: "54618",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-15T12:23:04",
    participant_id: "54671",
    brand: "Febreze",
    duration: "49",
  },
  {
    timestamp_local: "2025-03-15T12:26:20",
    participant_id: "54695",
    brand: "Febreze",
    duration: "121",
  },
  {
    timestamp_local: "2025-03-15T16:38:19",
    participant_id: "54619",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-16T06:56:40",
    participant_id: "53944",
    brand: "Febreze",
    duration: "37",
  },
  {
    timestamp_local: "2025-03-16T07:33:28",
    participant_id: "54635",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-16T09:32:20",
    participant_id: "54637",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-16T10:23:05",
    participant_id: "54608",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-16T11:03:17",
    participant_id: "54695",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-16T11:18:08",
    participant_id: "20130",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-16T11:41:02",
    participant_id: "10601",
    brand: "Febreze",
    duration: "24",
  },
  {
    timestamp_local: "2025-03-16T11:50:13",
    participant_id: "54619",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-03-16T12:15:19",
    participant_id: "54625",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-16T14:51:23",
    participant_id: "54618",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-16T16:35:22",
    participant_id: "54638",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-16T18:13:52",
    participant_id: "51135",
    brand: "Febreze",
    duration: "73",
  },
  {
    timestamp_local: "2025-03-16T18:13:53",
    participant_id: "15419",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-16T18:28:24",
    participant_id: "39185",
    brand: "Febreze",
    duration: "35",
  },
  {
    timestamp_local: "2025-03-16T19:55:50",
    participant_id: "54655",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-16T20:22:30",
    participant_id: "54631",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-16T21:13:02",
    participant_id: "43663",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-16T22:38:27",
    participant_id: "27064",
    brand: "Febreze",
    duration: "5",
  },
  {
    timestamp_local: "2025-03-17T06:53:54",
    participant_id: "54659",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-17T07:43:30",
    participant_id: "54635",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-17T07:54:12",
    participant_id: "54655",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-17T08:00:50",
    participant_id: "54608",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-17T08:38:30",
    participant_id: "54619",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-17T10:25:00",
    participant_id: "27064",
    brand: "Febreze",
    duration: "4",
  },
  {
    timestamp_local: "2025-03-17T11:20:19",
    participant_id: "38002",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-17T12:24:49",
    participant_id: "54695",
    brand: "Febreze",
    duration: "65",
  },
  {
    timestamp_local: "2025-03-17T13:06:43",
    participant_id: "54622",
    brand: "Febreze",
    duration: "75",
  },
  {
    timestamp_local: "2025-03-17T13:50:09",
    participant_id: "54623",
    brand: "Febreze",
    duration: "64",
  },
  {
    timestamp_local: "2025-03-17T15:06:59",
    participant_id: "54637",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-17T16:01:54",
    participant_id: "54635",
    brand: "Febreze",
    duration: "6",
  },
  {
    timestamp_local: "2025-03-17T16:14:07",
    participant_id: "10601",
    brand: "Febreze",
    duration: "45",
  },
  {
    timestamp_local: "2025-03-17T17:08:15",
    participant_id: "54652",
    brand: "Febreze",
    duration: "63",
  },
  {
    timestamp_local: "2025-03-17T17:10:18",
    participant_id: "34947",
    brand: "Febreze",
    duration: "8",
  },
  {
    timestamp_local: "2025-03-17T17:44:17",
    participant_id: "54601",
    brand: "Febreze",
    duration: "114",
  },
  {
    timestamp_local: "2025-03-17T18:08:10",
    participant_id: "54601",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-17T18:24:16",
    participant_id: "54618",
    brand: "Febreze",
    duration: "37",
  },
  {
    timestamp_local: "2025-03-17T22:36:04",
    participant_id: "53944",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-17T23:07:53",
    participant_id: "15419",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-18T06:20:23",
    participant_id: "54659",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-18T08:01:21",
    participant_id: "54608",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-18T09:11:44",
    participant_id: "51135",
    brand: "Febreze",
    duration: "61",
  },
  {
    timestamp_local: "2025-03-18T11:01:25",
    participant_id: "54637",
    brand: "Febreze",
    duration: "62",
  },
  {
    timestamp_local: "2025-03-18T11:22:09",
    participant_id: "54658",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-18T11:26:27",
    participant_id: "34947",
    brand: "Febreze",
    duration: "42",
  },
  {
    timestamp_local: "2025-03-18T12:05:33",
    participant_id: "15419",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-18T14:18:49",
    participant_id: "54625",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-18T15:31:33",
    participant_id: "54695",
    brand: "Febreze",
    duration: "55",
  },
  {
    timestamp_local: "2025-03-18T16:24:16",
    participant_id: "54659",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-18T20:38:42",
    participant_id: "38002",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-18T22:24:05",
    participant_id: "54659",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-19T07:06:37",
    participant_id: "53944",
    brand: "Febreze",
    duration: "44",
  },
  {
    timestamp_local: "2025-03-19T07:53:30",
    participant_id: "54608",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-19T08:20:47",
    participant_id: "34947",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-19T09:49:41",
    participant_id: "54659",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-19T14:34:22",
    participant_id: "54618",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-19T17:26:26",
    participant_id: "54623",
    brand: "Febreze",
    duration: "103",
  },
  {
    timestamp_local: "2025-03-19T17:44:33",
    participant_id: "54671",
    brand: "Febreze",
    duration: "38",
  },
  {
    timestamp_local: "2025-03-19T18:04:44",
    participant_id: "54601",
    brand: "Febreze",
    duration: "40",
  },
  {
    timestamp_local: "2025-03-19T19:04:35",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-19T19:08:46",
    participant_id: "54655",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-19T20:45:22",
    participant_id: "54631",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-20T07:09:33",
    participant_id: "54618",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-20T07:35:40",
    participant_id: "54652",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-20T07:57:15",
    participant_id: "54608",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-20T10:10:13",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-20T11:35:33",
    participant_id: "54637",
    brand: "Febreze",
    duration: "59",
  },
  {
    timestamp_local: "2025-03-20T13:13:01",
    participant_id: "54619",
    brand: "Febreze",
    duration: "40",
  },
  {
    timestamp_local: "2025-03-20T13:20:31",
    participant_id: "54635",
    brand: "Febreze",
    duration: "38",
  },
  {
    timestamp_local: "2025-03-20T17:34:57",
    participant_id: "54625",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-20T17:59:23",
    participant_id: "54622",
    brand: "Febreze",
    duration: "30",
  },
  {
    timestamp_local: "2025-03-20T19:08:24",
    participant_id: "43663",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-20T20:00:48",
    participant_id: "53944",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-03-20T21:05:02",
    participant_id: "39185",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-21T07:59:16",
    participant_id: "54638",
    brand: "Febreze",
    duration: "24",
  },
  {
    timestamp_local: "2025-03-21T08:02:11",
    participant_id: "54608",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-21T08:50:36",
    participant_id: "54619",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-21T09:14:53",
    participant_id: "15419",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-21T09:39:20",
    participant_id: "54659",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-21T11:25:21",
    participant_id: "38002",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-21T11:25:56",
    participant_id: "54619",
    brand: "Febreze",
    duration: "11",
  },
  {
    timestamp_local: "2025-03-21T11:33:19",
    participant_id: "54626",
    brand: "",
    duration: "216",
  },
  {
    timestamp_local: "2025-03-21T13:14:28",
    participant_id: "54625",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-21T13:24:47",
    participant_id: "54633",
    brand: "Febreze",
    duration: "73",
  },
  {
    timestamp_local: "2025-03-21T15:19:08",
    participant_id: "51135",
    brand: "Febreze",
    duration: "85",
  },
  {
    timestamp_local: "2025-03-21T15:28:34",
    participant_id: "53944",
    brand: "Febreze",
    duration: "49",
  },
  {
    timestamp_local: "2025-03-21T16:42:29",
    participant_id: "34947",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-21T17:12:22",
    participant_id: "54623",
    brand: "Febreze",
    duration: "45",
  },
  {
    timestamp_local: "2025-03-21T17:30:34",
    participant_id: "54618",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-21T18:38:54",
    participant_id: "27064",
    brand: "Febreze",
    duration: "2",
  },
  {
    timestamp_local: "2025-03-21T18:41:16",
    participant_id: "54658",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-21T22:51:31",
    participant_id: "54659",
    brand: "Febreze",
    duration: "13",
  },
  {
    timestamp_local: "2025-03-22T07:11:53",
    participant_id: "54659",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-22T08:19:14",
    participant_id: "54695",
    brand: "Febreze",
    duration: "198",
  },
  {
    timestamp_local: "2025-03-22T08:20:33",
    participant_id: "54618",
    brand: "Febreze",
    duration: "2",
  },
  {
    timestamp_local: "2025-03-22T08:47:24",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-22T09:26:11",
    participant_id: "54652",
    brand: "Febreze",
    duration: "47",
  },
  {
    timestamp_local: "2025-03-22T11:39:19",
    participant_id: "20130",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-22T11:43:42",
    participant_id: "54608",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-22T11:45:07",
    participant_id: "54638",
    brand: "Febreze",
    duration: "29",
  },
  {
    timestamp_local: "2025-03-22T16:24:42",
    participant_id: "54637",
    brand: "Febreze",
    duration: "84",
  },
  {
    timestamp_local: "2025-03-22T16:25:31",
    participant_id: "54655",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-22T17:01:18",
    participant_id: "51135",
    brand: "Febreze",
    duration: "41",
  },
  {
    timestamp_local: "2025-03-22T18:13:44",
    participant_id: "54655",
    brand: "Febreze",
    duration: "9",
  },
  {
    timestamp_local: "2025-03-22T18:53:34",
    participant_id: "51135",
    brand: "Febreze",
    duration: "39",
  },
  {
    timestamp_local: "2025-03-22T20:15:18",
    participant_id: "54619",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-22T20:37:31",
    participant_id: "53944",
    brand: "Febreze",
    duration: "37",
  },
  {
    timestamp_local: "2025-03-23T07:01:46",
    participant_id: "54618",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-23T09:43:38",
    participant_id: "54608",
    brand: "Febreze",
    duration: "38",
  },
  {
    timestamp_local: "2025-03-23T09:47:45",
    participant_id: "38002",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-23T09:51:59",
    participant_id: "27064",
    brand: "Febreze",
    duration: "4",
  },
  {
    timestamp_local: "2025-03-23T10:25:25",
    participant_id: "54652",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-23T10:34:31",
    participant_id: "54622",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-03-23T11:14:01",
    participant_id: "15419",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-23T11:27:27",
    participant_id: "54671",
    brand: "Febreze",
    duration: "41",
  },
  {
    timestamp_local: "2025-03-23T11:39:18",
    participant_id: "54619",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-23T14:19:19",
    participant_id: "53944",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-03-23T17:03:41",
    participant_id: "54655",
    brand: "Febreze",
    duration: "37",
  },
  {
    timestamp_local: "2025-03-23T20:11:41",
    participant_id: "54631",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-23T22:00:11",
    participant_id: "38002",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-24T07:15:31",
    participant_id: "54635",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-24T07:52:04",
    participant_id: "54655",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-03-24T07:59:29",
    participant_id: "54608",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-24T08:00:11",
    participant_id: "54618",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-24T10:04:55",
    participant_id: "54623",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-24T11:09:21",
    participant_id: "54619",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-24T11:27:02",
    participant_id: "54659",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-24T12:13:29",
    participant_id: "54625",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-24T12:57:21",
    participant_id: "54658",
    brand: "Febreze",
    duration: "41",
  },
  {
    timestamp_local: "2025-03-24T14:09:30",
    participant_id: "39185",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-24T15:18:54",
    participant_id: "15419",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-24T19:11:18",
    participant_id: "54633",
    brand: "Febreze",
    duration: "10",
  },
  {
    timestamp_local: "2025-03-24T20:48:28",
    participant_id: "38002",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-25T06:59:14",
    participant_id: "54659",
    brand: "Febreze",
    duration: "47",
  },
  {
    timestamp_local: "2025-03-25T07:47:24",
    participant_id: "54655",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-25T08:03:28",
    participant_id: "54608",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-25T08:53:38",
    participant_id: "54619",
    brand: "Febreze",
    duration: "53",
  },
  {
    timestamp_local: "2025-03-25T09:58:29",
    participant_id: "54637",
    brand: "Febreze",
    duration: "27",
  },
  {
    timestamp_local: "2025-03-25T12:31:41",
    participant_id: "54618",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-25T17:17:12",
    participant_id: "53944",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-03-25T17:45:50",
    participant_id: "54655",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-25T18:39:05",
    participant_id: "54695",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-25T19:49:02",
    participant_id: "54623",
    brand: "Febreze",
    duration: "37",
  },
  {
    timestamp_local: "2025-03-25T21:03:19",
    participant_id: "54631",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-26T07:52:50",
    participant_id: "54608",
    brand: "Febreze",
    duration: "17",
  },
  {
    timestamp_local: "2025-03-26T08:05:36",
    participant_id: "54622",
    brand: "Febreze",
    duration: "36",
  },
  {
    timestamp_local: "2025-03-26T08:16:38",
    participant_id: "54619",
    brand: "Febreze",
    duration: "26",
  },
  {
    timestamp_local: "2025-03-26T08:30:18",
    participant_id: "15419",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-26T11:16:02",
    participant_id: "54658",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-26T13:11:33",
    participant_id: "54652",
    brand: "Febreze",
    duration: "37",
  },
  {
    timestamp_local: "2025-03-26T14:16:54",
    participant_id: "54625",
    brand: "Febreze",
    duration: "14",
  },
  {
    timestamp_local: "2025-03-26T14:59:40",
    participant_id: "54633",
    brand: "Febreze",
    duration: "22",
  },
  {
    timestamp_local: "2025-03-26T16:27:37",
    participant_id: "54619",
    brand: "Febreze",
    duration: "10",
  },
  {
    timestamp_local: "2025-03-26T17:22:20",
    participant_id: "54618",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-26T18:25:19",
    participant_id: "54623",
    brand: "Febreze",
    duration: "93",
  },
  {
    timestamp_local: "2025-03-26T18:59:43",
    participant_id: "53944",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-27T06:41:35",
    participant_id: "34947",
    brand: "Febreze",
    duration: "8",
  },
  {
    timestamp_local: "2025-03-27T07:18:53",
    participant_id: "54618",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-27T08:05:20",
    participant_id: "54619",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-27T08:10:06",
    participant_id: "54637",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-27T08:10:56",
    participant_id: "54652",
    brand: "Febreze",
    duration: "83",
  },
  {
    timestamp_local: "2025-03-27T08:13:16",
    participant_id: "54608",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-27T08:50:12",
    participant_id: "20130",
    brand: "Febreze",
    duration: "19",
  },
  {
    timestamp_local: "2025-03-27T09:07:04",
    participant_id: "51135",
    brand: "Febreze",
    duration: "63",
  },
  {
    timestamp_local: "2025-03-27T10:31:26",
    participant_id: "54618",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-27T12:12:41",
    participant_id: "54619",
    brand: "Febreze",
    duration: "31",
  },
  {
    timestamp_local: "2025-03-27T13:11:38",
    participant_id: "54635",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-27T14:19:36",
    participant_id: "54671",
    brand: "Febreze",
    duration: "57",
  },
  {
    timestamp_local: "2025-03-27T14:32:52",
    participant_id: "54658",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-27T14:55:09",
    participant_id: "15419",
    brand: "Febreze",
    duration: "15",
  },
  {
    timestamp_local: "2025-03-27T17:11:52",
    participant_id: "54619",
    brand: "Febreze",
    duration: "12",
  },
  {
    timestamp_local: "2025-03-27T18:36:26",
    participant_id: "54655",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-27T19:12:07",
    participant_id: "53944",
    brand: "Febreze",
    duration: "18",
  },
  {
    timestamp_local: "2025-03-27T20:25:18",
    participant_id: "54631",
    brand: "Febreze",
    duration: "21",
  },
  {
    timestamp_local: "2025-03-27T21:15:59",
    participant_id: "53944",
    brand: "Febreze",
    duration: "16",
  },
  {
    timestamp_local: "2025-03-28T07:54:39",
    participant_id: "54635",
    brand: "Febreze",
    duration: "38",
  },
  {
    timestamp_local: "2025-03-28T09:29:21",
    participant_id: "39185",
    brand: "Febreze",
    duration: "25",
  },
  {
    timestamp_local: "2025-03-28T10:12:21",
    participant_id: "15419",
    brand: "Febreze",
    duration: "6",
  },
  {
    timestamp_local: "2025-03-28T15:09:06",
    participant_id: "54633",
    brand: "Febreze",
    duration: "28",
  },
  {
    timestamp_local: "2025-03-28T15:27:00",
    participant_id: "54652",
    brand: "Febreze",
    duration: "29",
  },
  {
    timestamp_local: "2025-03-28T18:18:00",
    participant_id: "54638",
    brand: "Febreze",
    duration: "32",
  },
  {
    timestamp_local: "2025-03-29T06:58:04",
    participant_id: "54695",
    brand: "Febreze",
    duration: "20",
  },
  {
    timestamp_local: "2025-03-29T20:56:14",
    participant_id: "54652",
    brand: "Febreze",
    duration: "23",
  },
  {
    timestamp_local: "2025-03-30T19:44:31",
    participant_id: "54652",
    brand: "Febreze",
    duration: "11",
  },
];

export default function UsageHeatmap(props) {
  const {
    productType = "",
    selectedFilters,
    setSelectedFilters,
    isFilterByApplied,
    setIsFilterByApplied,
    selectedFilterValues,
    setSelectedFilterValues,
  } = props;
  const {
    device,
    deviceCode,
    category,
    product_id,
    showSidebar,
    mappedCategory,
    project_id,
  } = useGlobalContext();

  const { filter_week } = useSelector((state) => state.dateRange);
  const { token } = useSelector((state) => state.auth);

  const [eventsData, setEventData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {}, [product_id]);

  useEffect(() => {
    if (productType !== "") {
      fetchSensorMotionData();
    }
  }, [productType, props?.apply, filter_week]);

  const fetchSensorMotionData = async () => {
    try {
      setLoading(true);
      const response = await getUsageHeatmapData(
        {
          project_id: parseInt(project_id),
          product_id: parseInt(product_id),
          product_type: productType,
          mapped_category: mappedCategory,
          filter_week: filter_week,
        },
        token,
      );
      setEventData(response.data.data);
    } catch (error) {
      console.error("Error fetching chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  const wrappedSpec = {
    ...spec,
    data: { values: eventsData }, // embed the data in spec
  };

  return (
    <div
      className="pt-5 pb-2 poistion-relative"
      style={{ maxWidth: "900px", margin: "0 auto" }}
    >
      {/* <h2>Usage Rhythm Heatmap</h2> */}

      {!loading ? (
        <VegaEmbed spec={wrappedSpec} options={{ mode: "vega-lite" }} />
      ) : (
        <ChartSkeleton />
      )}
    </div>
  );
}
